import React from 'react';
import { CheckCircle2 } from 'lucide-react';
import { oneRmEpley } from './TrainingSection';

// Read-only rendering of a training plan, modelled on the coach's original
// Excel layout. Desktop/print: merged table. Mobile: card view.
// If exercises carry a "week" value (A/B), they are split into week sections.
//
// If `workoutLogs` is passed in, we compute the best estimated 1RM per
// exercise (matched by name, case-insensitive) via the Epley formula and
// render two extra columns: "Cél 70%" (hypertrophy floor) and
// "Csúcs 95%" (1RM ceiling) so the athlete can pick a working weight
// without scrolling through their log history.

const roundToStep = (kg, step = 2.5) => Math.round(kg / step) * step;

// Relative Hungarian time — "3 nap", "2 hete", "1 hónapja"
const humanAgo = (iso) => {
  if (!iso) return '';
  const now = Date.now();
  const then = new Date(iso).getTime();
  if (Number.isNaN(then)) return '';
  const days = Math.floor((now - then) / (24 * 3600 * 1000));
  if (days < 1) return 'ma';
  if (days === 1) return 'tegnap';
  if (days < 7) return `${days} napja`;
  if (days < 30) {
    const w = Math.floor(days / 7);
    return w === 1 ? '1 hete' : `${w} hete`;
  }
  if (days < 365) {
    const m = Math.floor(days / 30);
    return m === 1 ? '1 hónapja' : `${m} hónapja`;
  }
  const y = Math.floor(days / 365);
  return y === 1 ? '1 éve' : `${y} éve`;
};

// Last workout log per exercise (case-insensitive name match). Returns
// { weight, reps, ago, iso }.
const buildLastLogMap = (workoutLogs = []) => {
  const byName = {};
  const sorted = [...(workoutLogs || [])].sort((a, b) =>
    new Date(b?.created_at || 0).getTime() - new Date(a?.created_at || 0).getTime());
  sorted.forEach((log) => {
    const key = (log?.exercise_name || '').trim().toLowerCase();
    if (!key || byName[key]) return;
    // Pick the heaviest set as the "top" for display
    const sets = Array.isArray(log?.sets) ? log.sets : null;
    let w = log?.weight_kg, r = log?.reps;
    if (sets && sets.length) {
      const top = [...sets].sort((a, b) => (parseFloat(b?.kg) || 0) - (parseFloat(a?.kg) || 0))[0];
      if (top) { w = top.kg; r = top.reps; }
    }
    if (w == null && r == null) return;
    byName[key] = { weight: w, reps: r, ago: humanAgo(log?.created_at), iso: log?.created_at };
  });
  return byName;
};

// Best estimated 1RM per exercise, matched by case-insensitive trimmed name.
// Uses the top set of each workout log (either `sets[]` if present, else the
// fallback `weight_kg`/`reps` fields). Returns { est1rm, sourceWeight, sourceReps }.
const buildBest1RmMap = (workoutLogs = []) => {
  const byName = {};
  (workoutLogs || []).forEach((log) => {
    const key = (log?.exercise_name || '').trim().toLowerCase();
    if (!key) return;
    let bestForLog = 0;
    let sourceW = null, sourceR = null;
    const sets = Array.isArray(log?.sets) ? log.sets : null;
    if (sets && sets.length) {
      sets.forEach((s) => {
        const est = oneRmEpley(s?.kg, s?.reps);
        if (est != null && est > bestForLog) {
          bestForLog = est; sourceW = s?.kg; sourceR = s?.reps;
        }
      });
    } else {
      const est = oneRmEpley(log?.weight_kg, log?.reps);
      if (est != null) { bestForLog = est; sourceW = log?.weight_kg; sourceR = log?.reps; }
    }
    if (bestForLog > 0) {
      const prev = byName[key];
      if (!prev || bestForLog > prev.est1rm) {
        byName[key] = { est1rm: bestForLog, sourceWeight: sourceW, sourceReps: sourceR };
      }
    }
  });
  return byName;
};

// Zone kg for a percent (returns null if no data).
const zoneKg = (best, pct) => {
  if (!best || !best.est1rm) return null;
  return roundToStep(best.est1rm * (pct / 100), 2.5);
};

const META_ROWS = [
  ['A felkészülés edzés-időszakának jellege', 'jelleg'],
  ['Felkészülés időbeli terjedelme (mikrociklus hetek)', 'weeks_range'],
  ['Edzésnapok', 'training_days'],
  ['Edzésidőtartam', 'duration'],
  ['Kezdeti állapot — testsúly (kg)', 'start_weight_kg'],
  ['Kezdeti állapot — testzsír %', 'start_bodyfat_pct'],
  ['Intenzitás', 'intensity_note'],
  ['Edzés célja', 'goal'],
  ['Program megosztása', 'split'],
  ['Edzésmódszerek', 'methods'],
  ['Általános bemelegítés', 'warmup_general'],
  ['Speciális bemelegítés', 'warmup_special'],
];

const EXTRA_ROWS = [
  ['Levezetés', 'levezetes'],
  ['Kiegészítő kardió edzés', 'kardio'],
  ['Regenerálódást elősegítő eljárások', 'regeneracio'],
  ['Napi kcal bevitel', 'taplalkozas_kcal'],
  ['Makró tápanyagok aránya', 'taplalkozas_makro'],
  ['Étkezések száma', 'etkezesek'],
  ['Étkezési időpontok', 'etkezes_idopontok'],
  ['Napi folyadékbevitel', 'folyadek'],
  ['Táplálék-kiegészítés', 'kiegeszitok'],
];

const th = 'border border-gray-300 bg-gray-100 px-2 py-1.5 text-[11px] font-bold uppercase tracking-wide text-gray-700 text-left';
const td = 'border border-gray-300 px-2 py-1.5 text-sm text-gray-900 align-top';

const spansFor = (exercises) => exercises.map((ex, i) => {
  if (i > 0 && exercises[i - 1].muscle_group === ex.muscle_group) return 0;
  let span = 1;
  for (let j = i + 1; j < exercises.length && exercises[j].muscle_group === ex.muscle_group; j += 1) span += 1;
  return span;
});

const ExerciseTable = ({ exercises, best1RmByName = {}, lastLogByName = {} }) => {
  const groupSpans = spansFor(exercises);
  return (
    <table className="w-full border-collapse min-w-[900px]">
      <thead>
        <tr>
          <th className={th}>Testrész / Izomcsoport</th>
          <th className={th}>Gyakorlat neve</th>
          <th className={`${th} text-center`}>Sorozat</th>
          <th className={`${th} text-center`}>Ismétlés</th>
          <th className={`${th} text-center`}>1RM %</th>
          <th className={`${th} text-center`}>Kg</th>
          <th className={`${th} text-center`} title="A saját edzésnaplód alapján számolt izomépítő minimum súly (becsült 1RM × 70%)">Cél 70%</th>
          <th className={`${th} text-center`} title="A saját edzésnaplód alapján számolt csúcs / közel 1RM (becsült 1RM × 95%)">Csúcs 95%</th>
          <th className={`${th} text-center`}>Tempó</th>
          <th className={`${th} text-center`}>Pihenő</th>
          <th className={th}>Egyéb módszer</th>
          <th className={th}>Megjegyzés</th>
        </tr>
      </thead>
      <tbody>
        {exercises.map((ex, i) => {
          const key = (ex.name || '').trim().toLowerCase();
          const best = best1RmByName[key];
          const last = lastLogByName[key];
          const kg70 = zoneKg(best, 70);
          const kg95 = zoneKg(best, 95);
          const lastKg = last ? parseFloat(last.weight) : null;
          const goalReached = kg70 != null && lastKg != null && lastKg >= kg70;
          const tooltip = best
            ? `Becsült 1RM: ${Math.round(best.est1rm)} kg (${best.sourceWeight}kg × ${best.sourceReps})`
            : 'Még nincs elég adat az edzésnaplódban ehhez a gyakorlathoz';
          return (
            <tr key={ex.id || i}>
              {groupSpans[i] > 0 && (
                <td className={`${td} font-bold bg-gray-50`} rowSpan={groupSpans[i]}>
                  {ex.muscle_group}
                </td>
              )}
              <td className={td}>
                <div className="flex items-start gap-1.5 flex-wrap">
                  <span>{ex.name}</span>
                  {goalReached && (
                    <span
                      data-testid={`plan-goal-reached-${i}`}
                      className="inline-flex items-center gap-0.5 rounded bg-emerald-600 text-white text-[10px] font-bold px-1.5 py-0.5 uppercase tracking-wide"
                      title={`Cél elérve — az utolsó rögzítés (${last.weight} kg × ${last.reps}) ≥ 70% zóna (${kg70} kg)`}
                    >
                      <CheckCircle2 className="w-3 h-3" strokeWidth={3} />
                      Cél elérve
                    </span>
                  )}
                </div>
                {last && (
                  <div
                    data-testid={`plan-last-log-${i}`}
                    className="text-[11px] text-emerald-700 italic mt-0.5"
                    title={`Utolsó rögzítés: ${new Date(last.iso).toLocaleString('hu-HU')}`}
                  >
                    Utoljára: <strong>{last.weight}kg × {last.reps}</strong> · {last.ago}
                  </div>
                )}
              </td>
              <td className={`${td} text-center`}>{ex.sets}</td>
              <td className={`${td} text-center`}>{ex.reps}</td>
              <td className={`${td} text-center`}>{ex.intensity_pct}</td>
              <td className={`${td} text-center`}>{ex.intensity_kg}</td>
              <td className={`${td} text-center whitespace-nowrap`} title={tooltip} data-testid={`plan-zone-70-${i}`}>
                {kg70 != null ? (
                  <span className="inline-block rounded bg-emerald-100 text-emerald-800 font-bold px-1.5 py-0.5 text-xs">{kg70} kg</span>
                ) : <span className="text-gray-300">—</span>}
              </td>
              <td className={`${td} text-center whitespace-nowrap`} title={tooltip} data-testid={`plan-zone-95-${i}`}>
                {kg95 != null ? (
                  <span className="inline-block rounded bg-amber-100 text-amber-800 font-bold px-1.5 py-0.5 text-xs">{kg95} kg</span>
                ) : <span className="text-gray-300">—</span>}
              </td>
              <td className={`${td} text-center whitespace-nowrap`}>{ex.tempo}</td>
              <td className={`${td} text-center whitespace-nowrap`}>{ex.rest}</td>
              <td className={td}>{ex.methods}</td>
              <td className={td}>{ex.note}</td>
            </tr>
          );
        })}
      </tbody>
    </table>
  );
};

const ExerciseCards = ({ exercises, best1RmByName = {}, lastLogByName = {} }) => {
  const groupSpans = spansFor(exercises);
  return (
    <div className="space-y-4" data-testid="training-plan-cards">
      {exercises.map((ex, i) => {
        const key = (ex.name || '').trim().toLowerCase();
        const best = best1RmByName[key];
        const last = lastLogByName[key];
        const kg70 = zoneKg(best, 70);
        const kg95 = zoneKg(best, 95);
        const lastKg = last ? parseFloat(last.weight) : null;
        const goalReached = kg70 != null && lastKg != null && lastKg >= kg70;
        return (
          <React.Fragment key={ex.id || i}>
            {groupSpans[i] > 0 && (
              <div className="text-[11px] font-bold uppercase tracking-widest text-yellow-700 border-b border-yellow-600/30 pb-1 pt-1">
                {ex.muscle_group || 'Gyakorlatok'}
              </div>
            )}
            <div className="rounded-xl border border-gray-200 bg-gray-50 p-3.5">
              <div className="flex items-start justify-between gap-2 mb-1">
                <div className="font-bold text-gray-900 text-[15px] leading-snug">{ex.name}</div>
                {goalReached && (
                  <span
                    data-testid={`plan-card-goal-reached-${i}`}
                    className="inline-flex items-center gap-0.5 rounded bg-emerald-600 text-white text-[10px] font-bold px-1.5 py-0.5 uppercase tracking-wide shrink-0"
                    title={`Cél elérve — az utolsó rögzítés (${last.weight} kg × ${last.reps}) ≥ 70% zóna (${kg70} kg)`}
                  >
                    <CheckCircle2 className="w-3 h-3" strokeWidth={3} />
                    Cél elérve
                  </span>
                )}
              </div>
              {last && (
                <div
                  data-testid={`plan-card-last-log-${i}`}
                  className="text-[11px] text-emerald-700 italic mb-2"
                >
                  Utoljára: <strong>{last.weight}kg × {last.reps}</strong> · {last.ago}
                </div>
              )}
              <div className="flex flex-wrap gap-1.5">
                {(ex.sets || ex.reps) && (
                  <span className="inline-flex items-center rounded-md bg-gray-900 text-white text-xs font-bold px-2 py-1">
                    {ex.sets || '–'} × {ex.reps || '–'}
                  </span>
                )}
                {ex.intensity_pct && (
                  <span className="inline-flex items-center rounded-md bg-yellow-100 text-yellow-900 text-xs font-semibold px-2 py-1">
                    {ex.intensity_pct}
                  </span>
                )}
                {ex.intensity_kg && (
                  <span className="inline-flex items-center rounded-md bg-yellow-100 text-yellow-900 text-xs font-semibold px-2 py-1">
                    {ex.intensity_kg} kg
                  </span>
                )}
                {kg70 != null && (
                  <span
                    data-testid={`plan-card-zone-70-${i}`}
                    className="inline-flex items-center rounded-md bg-emerald-100 text-emerald-900 text-xs font-bold px-2 py-1"
                    title={best ? `Cél 70% (izomépítő minimum) — becsült 1RM ${Math.round(best.est1rm)} kg alapján` : ''}
                  >
                    Cél 70% · {kg70} kg
                  </span>
                )}
                {kg95 != null && (
                  <span
                    data-testid={`plan-card-zone-95-${i}`}
                    className="inline-flex items-center rounded-md bg-amber-100 text-amber-900 text-xs font-bold px-2 py-1"
                    title={best ? `Csúcs 95% (közel 1RM) — becsült 1RM ${Math.round(best.est1rm)} kg alapján` : ''}
                  >
                    Csúcs 95% · {kg95} kg
                  </span>
                )}
                {ex.tempo && (
                  <span className="inline-flex items-center rounded-md bg-gray-200 text-gray-700 text-xs px-2 py-1">
                    Tempó {ex.tempo}
                  </span>
                )}
                {ex.rest && (
                  <span className="inline-flex items-center rounded-md bg-gray-200 text-gray-700 text-xs px-2 py-1">
                    Pihenő {ex.rest}
                  </span>
                )}
                {ex.methods && (
                  <span className="inline-flex items-center rounded-md bg-sky-100 text-sky-900 text-xs px-2 py-1">
                    {ex.methods}
                  </span>
                )}
              </div>
              {ex.note && <p className="text-xs text-gray-500 mt-2 italic">{ex.note}</p>}
            </div>
          </React.Fragment>
        );
      })}
    </div>
  );
};

export const TrainingPlanView = ({ plan, mobileCards = true, workoutLogs = [] }) => {
  const best1RmByName = React.useMemo(() => buildBest1RmMap(workoutLogs), [workoutLogs]);
  const lastLogByName = React.useMemo(() => buildLastLogMap(workoutLogs), [workoutLogs]);
  if (!plan) return null;
  const meta = plan.meta || {};
  const extras = plan.extras || {};
  const exercises = (plan.exercises || []).filter((e) => (e.name || '').trim());
  const hasAnyZone = Object.keys(best1RmByName).length > 0;

  // Split into week sections (A/B/…) if any exercise carries a week value.
  const hasWeeks = exercises.some((e) => (e.week || '').toString().trim());
  let sections;
  if (hasWeeks) {
    const order = [];
    const byWeek = {};
    exercises.forEach((e) => {
      const w = (e.week || '').toString().trim().toUpperCase() || 'MINDEN HÉT';
      if (!byWeek[w]) { byWeek[w] = []; order.push(w); }
      byWeek[w].push(e);
    });
    order.sort((a, b) => a.localeCompare(b, 'hu'));
    sections = order.map((w) => ({ label: w.length <= 2 ? `„${w}” hét` : w, exs: byWeek[w] }));
  } else {
    sections = [{ label: null, exs: exercises }];
  }

  const metaFilled = META_ROWS.filter(([, k]) => (meta[k] || '').toString().trim());
  const extrasFilled = EXTRA_ROWS.filter(([, k]) => (extras[k] || '').toString().trim());

  return (
    <div data-testid="training-plan-view" className="bg-white text-gray-900">
      <h2 className="text-xl md:text-2xl font-bold text-center mb-4" style={{ fontFamily: 'Montserrat, sans-serif' }}>
        {plan.title}
      </h2>

      {hasAnyZone && (
        <div
          data-testid="plan-zone-legend"
          className="mb-4 rounded-lg border border-emerald-200 bg-emerald-50/60 px-3 py-2 text-xs text-emerald-900 flex flex-wrap items-center gap-2"
        >
          <span className="font-bold">Cél zóna a saját edzésnaplód alapján:</span>
          <span className="inline-flex items-center rounded bg-emerald-100 text-emerald-800 font-bold px-2 py-0.5">Cél 70% — izomépítő minimum</span>
          <span className="inline-flex items-center rounded bg-amber-100 text-amber-800 font-bold px-2 py-0.5">Csúcs 95% — közel 1RM</span>
          <span className="inline-flex items-center gap-0.5 rounded bg-emerald-600 text-white font-bold px-2 py-0.5 uppercase tracking-wide">
            <CheckCircle2 className="w-3 h-3" strokeWidth={3} />
            Cél elérve
          </span>
          <span className="text-emerald-800/70">
            Epley-becslés a legjobb szetted alapján, 2,5 kg-ra kerekítve. „Cél elérve" ha az utolsó edzés súlya elérte a 70% zónát. Ahol még nincs elég adat, „—" jelenik meg.
          </span>
        </div>
      )}

      {metaFilled.length > 0 && (
        <table className="w-full border-collapse mb-5">
          <tbody>
            {metaFilled.map(([label, key]) => (
              <tr key={key}>
                <td className={`${th} w-64`}>{label}</td>
                <td className={td}>{meta[key]}</td>
              </tr>
            ))}
          </tbody>
        </table>
      )}

      {sections.map(({ label, exs }, si) => (
        <div key={label || si} className="mb-5">
          {label && (
            <div
              data-testid={`training-week-section-${si}`}
              className="inline-flex items-center rounded-md bg-gray-900 text-yellow-400 text-xs font-bold uppercase tracking-widest px-3 py-1.5 mb-3"
            >
              {label}
            </div>
          )}
          <div className={`${mobileCards ? 'hidden md:block' : ''} overflow-x-auto`}>
            <ExerciseTable exercises={exs} best1RmByName={best1RmByName} lastLogByName={lastLogByName} />
          </div>
          {mobileCards && (
            <div className="md:hidden">
              <ExerciseCards exercises={exs} best1RmByName={best1RmByName} lastLogByName={lastLogByName} />
            </div>
          )}
        </div>
      ))}

      {extrasFilled.length > 0 && (
        <table className="w-full border-collapse">
          <tbody>
            {extrasFilled.map(([label, key]) => (
              <tr key={key}>
                <td className={`${th} w-64`}>{label}</td>
                <td className={`${td} whitespace-pre-line`}>{extras[key]}</td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
    </div>
  );
};

export default TrainingPlanView;
